/**
 * Shipping Stats Widget
 *
 * Displays shipping statistics and dispatch status.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React from 'react';
import {
  PackageCheck,
  Send,
  Tag,
  Timer,
  CheckCircle2,
} from 'lucide-react';
import { WidgetInstance, ShippingStats } from '../../types';
import { getShippingStats } from '../../mockDataService';
import { fetchShippingStats } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useTranslation } from '../../../../../../contexts/I18nProvider';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface ShippingStatsWidgetProps {
  instance: WidgetInstance;
}

const ShippingStatsWidget: React.FC<ShippingStatsWidgetProps> = ({ instance }) => {
  const { t } = useTranslation();
  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<ShippingStats>({
    widgetId: 'shipping-stats',
    instance,
    fetchFn: async () => {
      try {
        return await fetchShippingStats();
      } catch {
        return getShippingStats();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="stat" count={5} />
      </WidgetWrapper>
    );
  }

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      {data && (
        <div className="h-full flex flex-col gap-3">
          {/* Main stats */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-lg p-3 flex items-center gap-3">
              <PackageCheck className="w-8 h-8 text-cyan-400" />
              <div>
                <div className="text-2xl font-bold text-white">{data.readyToShip}</div>
                <div className="text-xs text-gray-400">{t('manager.modules.wmsDashboard.shippingStats.readyToShip', {}, 'Klar til sending')}</div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-lg p-3 flex items-center gap-3">
              <Send className="w-8 h-8 text-green-400" />
              <div>
                <div className="text-2xl font-bold text-white">{data.shippedToday}</div>
                <div className="text-xs text-gray-400">{t('manager.modules.wmsDashboard.shippingStats.shippedToday', {}, 'Sendt i dag')}</div>
              </div>
            </div>
          </div>

          {/* Secondary stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-700/30 rounded-lg p-2 text-center">
              <Tag className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
              <div className="text-lg font-semibold text-white">{data.pendingLabels}</div>
              <div className="text-xs text-gray-400">{t('manager.modules.wmsDashboard.shippingStats.pendingLabels', {}, 'Venter labels')}</div>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-2 text-center">
              <Timer className="w-4 h-4 text-blue-400 mx-auto mb-1" />
              <div className="text-lg font-semibold text-white">{data.avgShipTime}m</div>
              <div className="text-xs text-gray-400">{t('manager.modules.wmsDashboard.shippingStats.avgShipTime', {}, 'Snitt pakketid')}</div>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-2 text-center">
              <CheckCircle2 className="w-4 h-4 text-green-400 mx-auto mb-1" />
              <div className="text-lg font-semibold text-white">{data.onTimeRate}%</div>
              <div className="text-xs text-gray-400">{t('manager.modules.wmsDashboard.shippingStats.onTime', {}, 'I tide')}</div>
            </div>
          </div>

          {/* On-time delivery progress */}
          <div className="flex-1 flex flex-col justify-end">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{t('manager.modules.wmsDashboard.shippingStats.deliveryPrecision', {}, 'Leveringspresisjon')}</span>
              <span className={data.onTimeRate >= 95 ? 'text-green-400' : 'text-yellow-400'}>
                {data.onTimeRate}%
              </span>
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 rounded-full ${
                  data.onTimeRate >= 95
                    ? 'bg-gradient-to-r from-green-500 to-green-400'
                    : 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                }`}
                style={{ width: `${data.onTimeRate}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default ShippingStatsWidget;
